import { FlexLayout } from '@deity/falcon-ui';
import type {
  DictDefinition,
  FieldDefinition,
  LinkDefinition,
  ListDefinition
} from '@deity-io/di-data-structure-editor';
import { Collapsible } from 'components/Collapsible';
import { SchemaActionTypes, SchemaContext } from 'contexts/SchemaContext';
import _ from 'lodash';
import type React from 'react';
import { useContext, useMemo } from 'react';
import AddConstraint from './AddConstraint';
import ConstraintItem from './ConstraintsItem';

type ConstraintsContainerProps = {
  isEditable: boolean;
  path: Array<string>;
  schema: DictDefinition | ListDefinition | FieldDefinition; // this can be the linked structure
  link?: LinkDefinition; // the original structures link field
};
const ConstraintsContainer: React.FC<ConstraintsContainerProps> = ({ isEditable, schema, path, link }) => {
  const ctx = useContext(SchemaContext);
  const constraints = [...(schema.config['validators'] || []), ...(link?.config['validators'] || [])];
  /**
   * If it's a link. lock out the constraints of the linked structure.
   * You can only edit the links of the constraints in this schema
   */
  const lockedCount = link ? schema?.config['validators']?.length || 0 : 0;

  const constraintsProperties = useMemo(() => {
    const validators = schema.availableValidators();
    const constraintsProps = {};
    validators.forEach(validatorKey => {
      constraintsProps[validatorKey] = schema.getValidatorProperties(validatorKey);
    });

    return constraintsProps;
  }, [schema.config.validators]);

  const updateConstraints = (updatedConstraints: any[]) => {
    ctx?.dispatch({
      type: SchemaActionTypes.UPDATE_SETTING,
      payload: {
        path: [...path],
        property: ['validators'],
        value: updatedConstraints
      }
    });
  };

  const onConstraintChange = (i: number, key: Array<string>, value: string | number | boolean | undefined | {}) => {
    ctx?.dispatch({
      type: SchemaActionTypes.UPDATE_SETTING,
      payload: {
        path: [...path],
        property: ['validators', i.toFixed(), ...key],
        value
      }
    });
  };

  if (_.isEmpty(constraintsProperties)) {
    return null;
  }

  return (
    <Collapsible title="Constraints" itemCount={constraints.length}>
      {isEditable && (
        <AddConstraint
          constraints={schema?.availableValidators()}
          constraintsProperties={constraintsProperties}
          onConstraintAdd={val => updateConstraints([...constraints.slice(lockedCount), { ...val }])}
        />
      )}
      <FlexLayout>
        {constraints.map((constraint, i) => {
          if (!constraint) {
            // eslint-disable-next-line no-console
            console.warn('Invalid constraint in structure.');
          }

          return (
            <ConstraintItem
              key={i} // eslint-disable-line react/no-array-index-key
              validatorProperties={constraintsProperties[constraint.type]}
              persistedValue={constraint}
              owner={schema}
              type={constraint.type}
              index={(i + 1).toString()}
              locked={!isEditable || i < lockedCount}
              onRemove={index => {
                ctx?.dispatch({
                  type: SchemaActionTypes.UPDATE_SETTING,
                  payload: {
                    path: [...path],
                    property: ['validators', (i - lockedCount).toFixed()],
                    value: undefined
                  }
                });
                // return updateConstraints(constraints.filter(p => p !== constraints[parseInt(index, 10) - 1]));
              }}
              onValueChange={(key, value) => {
                onConstraintChange(i - lockedCount, key, value);
              }}
              onDeleteProperty={key => {
                onConstraintChange(i - lockedCount, key, undefined);
              }}
            />
          );
        })}
      </FlexLayout>
    </Collapsible>
  );
};

export default ConstraintsContainer;
